import { useNavigate } from 'react-router-dom';

interface ChurchItem {
  id: number;
  title: string;
  description: string;
  thumbnail: string;
  date: string;
  clickable: boolean;
  link?: string;
}

const churchItems: ChurchItem[] = [
  {
    id: 1,
    title: '믿음을 전한 선교사들',
    description: '19세기 낯선 땅 조선을 향해 바다를 건넌 선교사들. 그들이 복음과 함께 전한 문자, 교육, 존엄의 이야기를 탐구합니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/47f83b7776b54202ea695b62e30b5efe.png',
    date: '2025.01.15',
    clickable: true,
    link: '/martyrs',
  },
  {
    id: 2,
    title: '믿음을 받아들인 선배들',
    description: '복음을 받아들이고 이 땅에 뿌리내린 조선의 신앙 선배들. 이름 없이 사라질 뻔한 그들의 이야기를 다시 꺼냅니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/7291d5009e8b924b5e1382eabb1c4eb5.png',
    date: '2024.12.20',
    clickable: true,
    link: '/faith-pioneers',
  },
  {
    id: 3,
    title: '교회사 타임라인',
    description: '1884년 알렌 입국부터 1919년 3·1운동까지, 한국 개신교 역사의 주요 사건들을 시각적으로 정리합니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/67aa8164092ec64f0108a7e4bb179735.png',
    date: '2024.11.28',
    clickable: true,
    link: '/timeline',
  },
  {
    id: 4,
    title: '교회사 성지순례',
    description: '한국 기독교 역사의 현장을 직접 걷는 성지순례. 평양, 원산, 이화학당 등 믿음의 발자취가 남은 곳들을 안내합니다.',
    thumbnail: 'https://static.readdy.ai/image/3c653b3a8a919d24e4f7a2804547f79c/182b3fab5e9cbb31edab09b8972d049a.png',
    date: '2024.10.15',
    clickable: false,
  },
];

const churchHistoryData = [
  {
    id: 1,
    title: '한국 순교자들의 이야기',
    description: '신앙을 지키기 위해 목숨을 바친 한국 순교자들의 감동적인 이야기를 발굴하고 기록합니다.',
    thumbnail: 'https://public.readdy.ai/ai/img_res/7dfadff30cfa9f1774509be9dbccd889.jpg',
    date: '2024.12.15',
    clickable: true,
    link: '/martyrs'
  },
  {
    id: 2,
    title: '초기 한국교회 선교사들',
    description: '알렌, 언더우드, 아펜젤러 등 한국에 복음을 전한 초기 선교사들의 헌신과 사역을 조명합니다.',
    thumbnail: 'https://readdy.ai/api/search-image?query=Early%20Korean%20Protestant%20missionaries%20in%20late%201800s%20Korea%2C%20vintage%20sepia%20photograph%20style%20showing%20Western%20missionaries%20with%20Korean%20converts%2C%20historical%20church%20building%20and%20Bible%2C%20documentary%20photography%20with%20aged%20paper%20texture%2C%20warm%20nostalgic%20tones&width=800&height=600&seq=church-missionaries-01&orientation=landscape',
    date: '2024.11.30',
    clickable: false
  },
  {
    id: 3,
    title: '교회사 타임라인',
    description: '1884년 알렌 입국부터 1919년 3·1운동까지, 한국 개신교 역사의 주요 사건들을 시각적으로 정리합니다.',
    thumbnail: 'https://readdy.ai/api/search-image?query=Korean%20church%20history%20timeline%20collage%20with%20vintage%20photographs%20of%20historical%20buildings%20churches%20and%20people%2C%20sepia%20toned%20documentary%20style%20with%20old%20Korean%20architecture%20and%20Christian%20symbols%2C%20nostalgic%20historical%20atmosphere%20with%20aged%20paper%20texture%2C%20warm%20brown%20and%20cream%20tones&width=800&height=600&seq=church-timeline-01&orientation=landscape',
    date: '2024.11.28',
    clickable: true,
    link: '/timeline'
  },
];

export default function ChurchSection() {
  const navigate = useNavigate();

  const handleCardClick = (item: ChurchItem) => {
    if (item.clickable && item.link) {
      navigate(item.link);
    }
  };

  return (
    <section id="church" className="py-16 md:py-24 px-4 md:px-6" style={{ backgroundColor: '#C2185B' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4 text-white leading-tight">
            한국 교회사 연구
          </h2>
          <p className="text-base md:text-lg max-w-3xl mx-auto font-medium text-white/90 leading-relaxed">
            19세기, 낯선 땅 조선을 향해 바다를 건넌 선교사들. 그들이 전한 복음을 받아들인 사람들,<br />
            이름 없이 사라질 뻔한 그 이야기들을, 다시 세상에 꺼냅니다.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {churchItems.map((item) => (
            <div
              key={item.id}
              onClick={() => handleCardClick(item)}
              className={`relative overflow-hidden rounded-xl shadow-lg group ${
                item.clickable ? 'cursor-pointer' : 'cursor-default'
              }`}
              style={{ minHeight: '400px' }}
            >
              {/* Full bleed image */}
              <div className="absolute inset-0 w-full h-full">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
                {/* Gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10"></div>
              </div>

              {/* Content overlay */}
              <div className="relative z-10 flex flex-col justify-end h-full p-5 md:p-6" style={{ minHeight: '380px' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm" style={{ backgroundColor: '#00E5CC', color: '#0a2e2b' }}>
                    교회사 연구
                  </span>
                  <span className="text-xs text-white/70">{item.date}</span>
                </div>

                <h3 className="text-lg md:text-xl font-bold text-white mb-2 leading-tight drop-shadow-lg">
                  {item.title}
                </h3>

                <p className="text-white/85 text-xs md:text-sm leading-relaxed line-clamp-3 drop-shadow">
                  {item.description}
                </p>

                {item.clickable && (
                  <div className="mt-4 flex items-center gap-1 text-xs font-bold group-hover:gap-3 transition-all duration-300" style={{ color: '#00E5CC' }}>
                    <i className="ri-arrow-right-line text-sm" />
                    자세히 보기
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}